import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { QueryParamSearchType } from '../../components/aoraki/generic-search-react/src/hooks/search'
import FullScreenMap from '../../components/wells/full-screen-map'
import { default as NationalSearch, default as RegionalSearch } from '../../components/wells/search'
import useUser from '../../hooks/use-user'
import { SearchTypeKey } from '../../services/constants'
import { isCouncilUser } from '../../services/user-utils'
import { usePageState } from '../../context/page-context'
import { Card, ConditionalCardWrapper, ContentContainer, TabbedContent, useTitleChange } from '@msaf/core-react'

export default function Search() {
  useTitleChange('Wells Aotearoa NZ | Search')
  const { setPageState } = usePageState()
  const { search } = useLocation()
  const { mode } = useParams() as { mode: string }
  const [isMapView, setIsMapView] = useState(false)
  const [activeSearchTypeKey, setActiveSearchTypeKey] = useState(SearchTypeKey.NATIONAL)
  const [activeTab, setActiveTab] = useState<number>(0)

  const defaultTab = {
    label: 'National',
    tabContent: <NationalSearch searchKey={SearchTypeKey.NATIONAL} />,
  }

  const defaultMapTab = {
    label: 'National',
    searchTypeKey: SearchTypeKey.NATIONAL,
  }
  const [tabs, setTabs] = useState<{ label: string; tabContent: JSX.Element }[]>([defaultTab])
  const [mapTabs, setMapTabs] = useState<{ label: string; searchTypeKey: string }[]>([defaultMapTab])
  const { user } = useUser()

  useEffect(() => {
    const regionalTab = {
      label: 'Regional',
      tabContent: <RegionalSearch searchKey={SearchTypeKey.REGIONAL} />,
    }

    const regionalMapTab = {
      label: 'Regional',
      searchTypeKey: SearchTypeKey.REGIONAL,
    }

    if (isCouncilUser(user)) {
      const tabsForCouncilUser = [regionalTab, defaultTab]

      if (isMapView) {
        setMapTabs([regionalMapTab, defaultMapTab])
      }

      // Check the search type key in the query params, if present
      // and then set the active tab for better UX
      let activeSearchTypeKey = SearchTypeKey.REGIONAL
      let activeTab = 0
      const queryParamSearchFilters = new URLSearchParams(search).get('queryParamSearchFilters')
      if (queryParamSearchFilters) {
        const { searchTypeKey } = JSON.parse(queryParamSearchFilters) as QueryParamSearchType
        // Check if the search type key passed in params match the current search template
        if (searchTypeKey === SearchTypeKey.NATIONAL) {
          activeTab = tabsForCouncilUser.findIndex((tab) => tab.label === 'National')
          activeSearchTypeKey = SearchTypeKey.NATIONAL
        } else if (searchTypeKey === SearchTypeKey.REGIONAL) {
          activeTab = tabsForCouncilUser.findIndex((tab) => tab.label === 'Regional')
        }
      }

      setTabs(tabsForCouncilUser)
      setActiveTab(activeTab)
      setActiveSearchTypeKey(activeSearchTypeKey)
    }
    // Disabling due to suggestion of tabs to be included in dependancy array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.council, user?.token, isMapView])

  useEffect(() => {
    // Switch between map and table search mode
    setIsMapView(mode === 'map')
  }, [mode])

  useEffect(() => {
    setPageState({
      heading: 'Well search',
      isFullScreen: mode === 'map',
    })
    return () => setPageState({})
  }, [setPageState, mode])

  const handleTabChange = (index: number) => {
    const activeTab = tabs[index]
    setActiveTab(index)
    const searchTypeKey = activeTab.label === 'Regional' ? SearchTypeKey.REGIONAL : SearchTypeKey.NATIONAL
    setActiveSearchTypeKey(searchTypeKey)
  }

  return (
    <>
      {!isMapView ? (
        <>
          <ContentContainer>
            <ConditionalCardWrapper
              condition={!isMapView}
              wrapper={(children) => (
                <Card isFullWidth hasTabbedContent>
                  {children}
                </Card>
              )}
            >
              <TabbedContent
                tabs={tabs}
                onTabChange={handleTabChange}
                borderBottomStyle='shadow'
                includeHorizontalPadding
                ariaLabel='Wells search'
                activeTab={activeTab}
              />
            </ConditionalCardWrapper>
          </ContentContainer>
        </>
      ) : (
        <FullScreenMap
          searchKey={activeSearchTypeKey}
          isDefaultTabbedContent={false}
          onTabChange={handleTabChange}
          mapTabs={mapTabs}
          activeTab={activeTab}
        />
      )}
    </>
  )
}
